import camelCase from 'lodash/camelCase'
import { AccountType } from './types'
import { ContactPersonValues } from '../shared/Forms/ContactPerson/types'
import { FormErrorType } from "@sharedComponents/PantaForm/types";
import {
    appendError,
    removeAllErrorsForKey, removeError,
    replaceError,
    validateIsBankgiro, validateIsEmail, validateIsPlusgiro, validateIsZipCode,
    validateRequired
} from "@sharedComponents/PantaForm/utils";
import i18n from "@apps/Shared/translation/i18n";
import {
    IValidateOrganisationNumberResponse,
    OrgNrValidationStatus
} from "@apps/Shared/Services/CompanyRegistrations/interfaces";
import CompanyRegistrationsService from "@apps/Shared/Services/CompanyRegistrations/CompanyRegistrations.service";
import { StatusCode } from "@apps/Shared/Services/Response.types";
import { InputSelectOption } from "@sharedComponents/InputSelect/types";

export enum OrganisationType {
    Supplier = 'Supplier',
    Customer = 'Customer',
}

export const validateOrganisationNumber = async (
    organisationType: OrganisationType,
    organizationNumber: string | undefined,
    currentErrors: FormErrorType[],
    apiValidate: boolean,
    required: boolean
) => {
    let errors = required
        ? validateRequired(
              organizationNumber,
              'organizationNumber',
              i18n.t('error.emptyOrgnr'),
              currentErrors
          )
        : ([] as FormErrorType[])

    if (organizationNumber && apiValidate) {
        let info: any = undefined
        const type =
            organisationType === OrganisationType.Supplier
                ? 'supplier'
                : 'customer'

        const validationResult: IValidateOrganisationNumberResponse = await CompanyRegistrationsService.ValidateOrganisationNumber(
            type,
            organizationNumber
        ).then(res => {
            if (res.status === StatusCode.OK) {
                return res.data
            }
        })

        if (!validationResult) {
            errors = replaceError(
                errors,
                'organizationNumber',
                i18n.t('error.invalidOrgnr')
            )
        } else if (
            validationResult.validation === OrgNrValidationStatus.Exists
        ) {
            if (organisationType === OrganisationType.Supplier) {
                errors = replaceError(
                    errors,
                    'organizationNumber',
                    i18n.t('error.alreadyExistsOrgnr')
                )
            } else if (organisationType === OrganisationType.Customer) {
                info = {
                    label: i18n.t(
                        'companyRegistration.customer.existingCustomerInfoLabel'
                    ),
                    description: i18n.t(
                        'companyRegistration.customer.existingCustomerInfoDescription'
                    ),
                }
            }
        } else if (
            validationResult.validation === OrgNrValidationStatus.Invalid
        ) {
            errors = replaceError(
                errors,
                'organizationNumber',
                i18n.t('error.invalidOrgnr')
            )
        } else if (
            validationResult.validation === OrgNrValidationStatus.OnGoing
        ) {
            errors = replaceError(
                errors,
                'organizationNumber',
                i18n.t('error.onGoingRegistrationOrgNumber')
            )
        } else {
            errors = removeAllErrorsForKey(errors, 'organizationNumber')
        }

        return {
            errors: errors,
            info: info,
            validationResult: validationResult?.validation,
            existingInfo: {
                invoiceAddress: validationResult?.invoiceAddress,
                postalAddress: validationResult?.postalAddress,
                invoicingEmail: validationResult?.invoicingEmail,
                name: validationResult?.name,
                signatory: validationResult?.signatory,
                hasVat: validationResult?.hasVat,
                vatNumber: validationResult?.vatNumber,
            },
        }
    }

    return { errors: errors }
}

export const validateChangeOwner = (
    changeOwner: string | undefined,
    currentErrors: FormErrorType[]
) => {
    return validateRequired(
        changeOwner,
        'changeOwner',
        i18n.t('error.emptyChangeOwner'),
        currentErrors
    )
}

export const validateChangeOwnerDate = (
    changeOwnerDate: string | undefined,
    shouldValidate: boolean,
    currentErrors: FormErrorType[]
) => {
    if (!shouldValidate) {
        return removeAllErrorsForKey(currentErrors, 'changeOwnerDate')
    }

    return validateRequired(
        changeOwnerDate,
        'changeOwnerDate',
        i18n.t('error.emptyChangeOwnerDate'),
        currentErrors
    )
}

export const validateLegalName = (
    legalName: string | undefined,
    currentErrors: FormErrorType[]
) => {
    const errors = validateRequired(
        legalName,
        'legalName',
        i18n.t('error.emptyLegalName'),
        currentErrors
    )

    return errors
}

export const validateCompanyName = (
    companyName: string | undefined,
    currentErrors: FormErrorType[]
) => {
    return validateRequired(
        companyName,
        'companyName',
        i18n.t('error.emptyCompanyName'),
        currentErrors
    )
}

export const validateAccountType = (
    accountType: AccountType | undefined,
    shouldValidate: boolean,
    currentErrors: FormErrorType[]
) => {
    if (!shouldValidate) {
        return removeAllErrorsForKey(currentErrors, 'accountType')
    }

    return validateRequired(
        accountType,
        'accountType',
        i18n.t('error.emptyAccountType'),
        currentErrors
    )
}

export const validateBg = (
    bg: string | undefined,
    shouldValidate: boolean,
    currentErrors: FormErrorType[]
) => {
    if (!shouldValidate) {
        return removeAllErrorsForKey(currentErrors, 'bg')
    }

    let errors = validateRequired(
        bg,
        'bg',
        i18n.t('error.emptyBg'),
        currentErrors
    )

    if (bg) {
        errors = validateIsBankgiro(
            bg,
            'bg',
            i18n.t('error.invalidBg'),
            currentErrors
        )
    } else {
        removeError(currentErrors, 'bg', i18n.t('error.invalidBg'))
    }

    return errors
}

export const validatePg = (
    pg: string | undefined,
    shouldValidate: boolean,
    currentErrors: FormErrorType[]
) => {
    if (!shouldValidate) {
        return removeAllErrorsForKey(currentErrors, 'pg')
    }

    let errors = validateRequired(
        pg,
        'pg',
        i18n.t('error.emptyPg'),
        currentErrors
    )

    if (pg) {
        errors = validateIsPlusgiro(
            pg,
            'pg',
            i18n.t('error.invalidPg'),
            currentErrors
        )
    } else {
        removeError(currentErrors, 'pg', i18n.t('error.invalidPg'))
    }

    return errors
}

export const validateDonateAccountType = (
    donateAccountType: AccountType | undefined,
    shouldValidate: boolean,
    currentErrors: FormErrorType[]
) => {
    if (!shouldValidate) {
        return removeAllErrorsForKey(currentErrors, 'donateAccountType')
    }

    return validateRequired(
        donateAccountType,
        'donateAccountType',
        i18n.t('error.emptyDonateAccountType'),
        currentErrors
    )
}

export const validateDonateBg = (
    donateBg: string | undefined,
    shouldValidate: boolean,
    currentErrors: FormErrorType[]
) => {
    if (!shouldValidate) {
        return removeAllErrorsForKey(currentErrors, 'donateBg')
    }

    let errors = validateRequired(
        donateBg,
        'donateBg',
        i18n.t('error.emptyDonateBg'),
        currentErrors
    )

    if (donateBg) {
        errors = validateIsBankgiro(
            donateBg,
            'donateBg',
            i18n.t('error.invalidDonateBg'),
            currentErrors
        )
    } else {
        removeError(currentErrors, 'donateBg', i18n.t('error.invalidDonateBg'))
    }

    return errors
}

export const validateDonatePg = (
    donatePg: string | undefined,
    shouldValidate: boolean,
    currentErrors: FormErrorType[]
) => {
    if (!shouldValidate) {
        return removeAllErrorsForKey(currentErrors, 'donatePg')
    }

    let errors = validateRequired(
        donatePg,
        'donatePg',
        i18n.t('error.emptyDonatePg'),
        currentErrors
    )

    if (donatePg) {
        errors = validateIsPlusgiro(
            donatePg,
            'donatePg',
            i18n.t('error.invalidDonatePg'),
            currentErrors
        )
    } else {
        removeError(currentErrors, 'donatePg', i18n.t('error.invaliDonatedPg'))
    }

    return errors
}

export const validateStreetBox = (
    streetBox: string | undefined,
    currentErrors: FormErrorType[]
) => {
    return validateRequired(
        streetBox,
        'streetBox',
        i18n.t('error.emptyStreetBox'),
        currentErrors
    )
}

export const validateCity = (
    city: string | undefined,
    currentErrors: FormErrorType[]
) => {
    return validateRequired(
        city,
        'city',
        i18n.t('error.emptyCity'),
        currentErrors
    )
}

export const validateZipCode = (
    zipCode: string | undefined,
    validateFormat: Boolean,
    currentErrors: FormErrorType[]
) => {
    let errors = validateRequired(
        zipCode,
        'zipCode',
        i18n.t('error.emptyZipCode'),
        currentErrors
    )

    if (zipCode) {
        if (validateFormat) {
            errors = validateIsZipCode(
                zipCode,
                'zipCode',
                i18n.t('error.incorrectZipCode'),
                currentErrors
            )
        } else {
            return removeAllErrorsForKey(currentErrors, 'zipCode')
        }
    }

    return errors
}

export const validateMunicipality = (
    municipality: InputSelectOption | undefined,
    currentErrors: FormErrorType[]
) => {
    return validateRequired(
        municipality,
        'municipality',
        i18n.t('error.emptyMunicipality'),
        currentErrors
    )
}

export const validateNewActivityMessage = (
    message: string | undefined,
    shouldValidate: boolean,
    currentErrors: FormErrorType[]
) => {
    if (!shouldValidate) {
        return removeAllErrorsForKey(currentErrors, 'message')
    }
    return validateRequired(
        message,
        'message',
        i18n.t('error.emptyNewActivityMessage'),
        currentErrors
    )
}

export const validateHasRvm = (
    hasRvm: string | undefined,
    currentErrors: FormErrorType[]
) => {
    return validateRequired(
        hasRvm,
        'hasRvm',
        i18n.t('error.emptyRvm'),
        currentErrors
    )
}

export const validateRvmEmail = (
    rvmEmail: string | undefined,
    shouldValidate: boolean,
    currentErrors: FormErrorType[]
) => {
    if (!shouldValidate) {
        return removeAllErrorsForKey(currentErrors, 'rvmEmail')
    }

    let errors = validateRequired(
        rvmEmail,
        'rvmEmail',
        i18n.t('error.emptyEmail'),
        currentErrors
    )

    if (rvmEmail) {
        errors = validateIsEmail(
            rvmEmail,
            'rvmEmail',
            i18n.t('error.incorrectEmail'),
            currentErrors
        )
    }

    return errors
}

export const validateSignatoryFirstName = (
    signatoryFirstName: string | undefined,
    currentErrors: FormErrorType[]
) => {
    return validateRequired(
        signatoryFirstName,
        'signatory.firstName',
        i18n.t('error.emptyFirstname'),
        currentErrors
    )
}

export const validateSignatoryLastName = (
    signatoryLastName: string | undefined,
    currentErrors: FormErrorType[]
) => {
    return validateRequired(
        signatoryLastName,
        'signatory.lastName',
        i18n.t('error.emptyLastname'),
        currentErrors
    )
}

export const validateSignatoryEmail = (
    signatoryEmail: string | undefined,
    currentErrors: FormErrorType[]
) => {
    let errors = validateRequired(
        signatoryEmail,
        'signatory.email',
        i18n.t('error.emptyEmail'),
        currentErrors
    )

    if (signatoryEmail) {
        errors = validateIsEmail(
            signatoryEmail,
            'signatory.email',
            i18n.t('error.incorrectEmail'),
            currentErrors
        )
    }

    return errors
}

export const validateDeliveryAddressStreetAddress = (
    deliveryAddressStreetAddress: string | undefined,
    shouldValidate: boolean,
    currentErrors: FormErrorType[]
) => {
    if (!shouldValidate) {
        return removeAllErrorsForKey(
            currentErrors,
            'deliveryAddress.streetAddress'
        )
    }

    return validateRequired(
        deliveryAddressStreetAddress,
        'deliveryAddress.streetAddress',
        i18n.t('error.emptyStreetAddress'),
        currentErrors
    )
}

export const validateDeliveryAddressZipCode = (
    deliveryAddressZipCode: string | undefined,
    shouldValidate: boolean,
    validateFormat: Boolean,
    currentErrors: FormErrorType[]
) => {
    if (!shouldValidate) {
        return removeAllErrorsForKey(currentErrors, 'deliveryAddress.zipCode')
    }

    let errors = validateRequired(
        deliveryAddressZipCode,
        'deliveryAddress.zipCode',
        i18n.t('error.emptyZipCode'),
        currentErrors
    )

    if (deliveryAddressZipCode) {
        if (validateFormat) {
            errors = validateIsZipCode(
                deliveryAddressZipCode,
                'deliveryAddress.zipCode',
                i18n.t('error.incorrectZipCode'),
                currentErrors
            )
        } else {
            return removeAllErrorsForKey(
                currentErrors,
                'deliveryAddress.zipCode'
            )
        }
    }

    return errors
}

export const validateDeliveryAddressCity = (
    deliveryAddressCity: string | undefined,
    shouldValidate: boolean,
    currentErrors: FormErrorType[]
) => {
    if (!shouldValidate) {
        return removeAllErrorsForKey(currentErrors, 'deliveryAddress.city')
    }

    return validateRequired(
        deliveryAddressCity,
        'deliveryAddress.city',
        i18n.t('error.emptyCity'),
        currentErrors
    )
}

export const validateInvoiceAddressStreetAddress = (
    invoiceAddressStreetAddress: string | undefined,
    currentErrors: FormErrorType[]
) => {
    return validateRequired(
        invoiceAddressStreetAddress,
        'invoiceAddress.streetAddress',
        i18n.t('error.emptyStreetAddress'),
        currentErrors
    )
}

export const validateInvoiceAddressZipCode = (
    invoiceAddressZipCode: string | undefined,
    validateFormat: Boolean,
    currentErrors: FormErrorType[]
) => {
    let errors = validateRequired(
        invoiceAddressZipCode,
        'invoiceAddress.zipCode',
        i18n.t('error.emptyZipCode'),
        currentErrors
    )

    if (invoiceAddressZipCode) {
        if (validateFormat) {
            errors = validateIsZipCode(
                invoiceAddressZipCode,
                'invoiceAddress.zipCode',
                i18n.t('error.incorrectZipCode'),
                currentErrors
            )
        } else {
            return removeAllErrorsForKey(
                currentErrors,
                'invoiceAddress.zipCode'
            )
        }
    }

    return errors
}

export const validateInvoiceAddressCity = (
    invoiceAddressCity: string | undefined,
    currentErrors: FormErrorType[]
) => {
    return validateRequired(
        invoiceAddressCity,
        'invoiceAddress.city',
        i18n.t('error.emptyCity'),
        currentErrors
    )
}

export const validateInvoiceAddressEmail = (
    invoiceAddressEmail: string | undefined,
    currentErrors: FormErrorType[]
) => {
    let errors = [...currentErrors]

    errors = validateRequired(
        invoiceAddressEmail,
        'invoiceAddress.email',
        i18n.t('error.emptyEmail'),
        errors
    )

    if (invoiceAddressEmail) {
        errors = validateIsEmail(
            invoiceAddressEmail,
            'invoiceAddress.email',
            i18n.t('error.incorrectEmail'),
            errors
        )
    }

    return errors
}

export const validateVisitingAddressStreetAddress = (
    visitingAddressStreetAddress: string | undefined,
    currentErrors: FormErrorType[]
) => {
    return validateRequired(
        visitingAddressStreetAddress,
        'visitingAddress.streetAddress',
        i18n.t('error.emptyStreetAddress'),
        currentErrors
    )
}

export const validateVisitingAddressZipCode = (
    visitingAddressZipCode: string | undefined,
    validateFormat: Boolean,
    currentErrors: FormErrorType[]
) => {
    let errors = validateRequired(
        visitingAddressZipCode,
        'visitingAddress.zipCode',
        i18n.t('error.emptyZipCode'),
        currentErrors
    )

    if (visitingAddressZipCode) {
        if (validateFormat) {
            errors = validateIsZipCode(
                visitingAddressZipCode,
                'visitingAddress.zipCode',
                i18n.t('error.incorrectZipCode'),
                currentErrors
            )
        } else {
            return removeAllErrorsForKey(
                currentErrors,
                'visitingAddress.zipCode'
            )
        }
    }

    return errors
}

export const validateVisitingAddressCity = (
    visitingAddressCity: string | undefined,
    currentErrors: FormErrorType[]
) => {
    return validateRequired(
        visitingAddressCity,
        'visitingAddress.city',
        i18n.t('error.emptyCity'),
        currentErrors
    )
}

export const validatePostalAddressStreetBox = (
    postalAddressStreetBox: string | undefined,
    shouldValidate: boolean,
    currentErrors: FormErrorType[]
) => {
    if (!shouldValidate) {
        return removeAllErrorsForKey(currentErrors, 'postalAddress.streetBox')
    }

    return validateRequired(
        postalAddressStreetBox,
        'postalAddress.streetBox',
        i18n.t('error.emptyStreetBox'),
        currentErrors
    )
}

export const validatePostalAddressCity = (
    postalAddressCity: string | undefined,
    shouldValidate: boolean,
    currentErrors: FormErrorType[]
) => {
    if (!shouldValidate) {
        return removeAllErrorsForKey(currentErrors, 'postalAddress.city')
    }

    return validateRequired(
        postalAddressCity,
        'postalAddress.city',
        i18n.t('error.emptyCity'),
        currentErrors
    )
}

export const validatePostalAddressZipCode = (
    postalAddressZipCode: string | undefined,
    shouldValidate: boolean,
    validateFormat: boolean,
    currentErrors: FormErrorType[]
) => {
    if (!shouldValidate) {
        return removeAllErrorsForKey(currentErrors, 'postalAddress.zipCode')
    }

    let errors = validateRequired(
        postalAddressZipCode,
        'postalAddress.zipCode',
        i18n.t('error.emptyZipCode'),
        currentErrors
    )

    if (postalAddressZipCode) {
        if (validateFormat) {
            errors = validateIsZipCode(
                postalAddressZipCode,
                'postalAddress.zipCode',
                i18n.t('error.incorrectZipCode'),
                currentErrors
            )
        } else {
            return removeAllErrorsForKey(currentErrors, 'postalAddress.zipCode')
        }
    }

    return errors
}

export const validateFirstname = (
    firstname: string | undefined,
    currentErrors: FormErrorType[]
) => {
    return validateRequired(
        firstname,
        'firstname',
        i18n.t('error.emptyFirstname'),
        currentErrors
    )
}

export const validateLastname = (
    lastname: string | undefined,
    currentErrors: FormErrorType[]
) => {
    return validateRequired(
        lastname,
        'lastname',
        i18n.t('error.emptyLastname'),
        currentErrors
    )
}

export const validateEmail = (
    email: string | undefined,
    shouldValidate: boolean,
    currentErrors: FormErrorType[]
) => {
    if (!shouldValidate) {
        return removeAllErrorsForKey(currentErrors, 'email')
    }

    let errors = []

    errors = validateRequired(
        email,
        'email',
        i18n.t('error.emptyEmail'),
        currentErrors
    )

    if (email) {
        errors = validateIsEmail(
            email,
            'email',
            i18n.t('error.incorrectEmail'),
            currentErrors
        )
    }

    return errors
}

export const validateMobile = (
    mobile: string | undefined,
    currentErrors: FormErrorType[]
) => {
    return validateRequired(
        mobile,
        'mobile',
        i18n.t('error.emptyMobileNumber'),
        currentErrors
    )
}

export const validateContactPerson = (
    values: ContactPersonValues,
    currentErrors: FormErrorType[],
    shouldValidate?: boolean
) => {
    let errors = [...currentErrors]

    if (!shouldValidate) {
        removeAllErrorsForKey(errors, 'firstname')
        removeAllErrorsForKey(errors, 'lastname')
        removeAllErrorsForKey(errors, 'email')
        removeAllErrorsForKey(errors, 'mobile')

        return errors
    }

    errors = validateFirstname(values.firstname, errors)
    errors = validateLastname(values.lastname, errors)
    errors = validateEmail(values.email, true, errors)
    errors = validateMobile(values.mobile, errors)

    return errors
}

export const validateToC = (terms: boolean, currentErrors: FormErrorType[]) => {
    let errors = [...currentErrors]

    if (!terms) {
        errors = appendError(errors, 'toc', i18n.t('error.missingToc'))
    } else {
        errors = removeError(errors, 'toc', i18n.t('error.missingToc'))
    }

    return errors
}

export const validateVat = (
    vat: string | undefined,
    currentErrors: FormErrorType[]
) => {
    return validateRequired(
        vat,
        'vatNumber',
        i18n.t('error.emptyVatnr'),
        currentErrors
    )
}

export const handleRequestErrors = async (
    currentErrors: FormErrorType[],
    requestErrors: Record<string, string[]>
) => {
    let errors = [...currentErrors]

    if (requestErrors) {
        for (const key of Object.keys(requestErrors)) {
            const errorMessage = requestErrors[key]

            errors = appendError(errors, camelCase(key), errorMessage[0])
        }
    }

    return errors
}